/**
 * Pure per-column filters + group-by for the Kronos entry list.
 *
 * Sits alongside `entryFilter.ts` (the global search + sort). The
 * pipeline applied by the view is:
 *
 *   raw → filterEntries(search) → filterByColumn(col filters)
 *       → sortEntries(key, dir) → groupEntries(group key)
 *
 * Every step is deterministic and dependency-free — the view layer
 * just renders the final grouped structure.
 */

import type { TimeEntry } from '@altyor-kronos/shared-types';
import type { EntryLookups } from './entryFilter';
import { sumDurationSeconds } from './timeEntries';

// ─── Per-column filter ─────────────────────────────────────────────

export interface ColumnFilters {
  /**
   * Keep only entries whose `designationId` is in the set. An empty
   * Set disables the filter (UX choice: "nothing selected" = "all").
   */
  designationIds?: ReadonlySet<string>;
  /** Same semantics for teammate. */
  userIds?: ReadonlySet<string>;
  /**
   * `true` = billable only, `false` = non-billable only,
   * `null`/undefined = both (no filter).
   */
  billable?: boolean | null;
}

export function filterByColumn(
  entries: readonly TimeEntry[],
  filters: ColumnFilters,
): TimeEntry[] {
  const { designationIds, userIds, billable } = filters;
  const designationFilter =
    designationIds && designationIds.size > 0 ? designationIds : null;
  const userFilter = userIds && userIds.size > 0 ? userIds : null;
  const billableFilter = billable === true || billable === false ? billable : null;

  if (!designationFilter && !userFilter && billableFilter === null) {
    return [...entries];
  }

  return entries.filter((e) => {
    if (designationFilter && !designationFilter.has(e.designationId)) return false;
    if (userFilter && !userFilter.has(e.userId)) return false;
    if (billableFilter !== null && e.billable !== billableFilter) return false;
    return true;
  });
}

// ─── Group-by ───────────────────────────────────────────────────────

export type EntryGroupKey = 'none' | 'designation' | 'teammate' | 'day' | 'week';

export interface EntryGroup {
  /** Grouping key — id, uid, YYYY-MM-DD, or 'all' for groupKey='none'. */
  key: string;
  /** Human-readable label, resolved from lookups when applicable. */
  label: string;
  entries: TimeEntry[];
  totalSeconds: number;
}

function dayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Monday 00:00 of the entry's week, as YYYY-MM-DD (string-sortable). */
function weekKey(date: Date): string {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  d.setDate(d.getDate() - dow);
  return dayKey(d);
}

/**
 * Group entries by the selected key. Groups are sorted alphabetically
 * by label, except `day` which sorts most-recent-first — a list of
 * work-days reads more naturally newest-to-oldest.
 *
 * `groupKey = 'none'` returns a single "all" group with the entries
 * in their original order, letting the view skip the group header
 * rendering without a separate code path.
 */
export function groupEntries(
  entries: readonly TimeEntry[],
  groupKey: EntryGroupKey,
  lookups: EntryLookups,
): EntryGroup[] {
  if (entries.length === 0) return [];

  if (groupKey === 'none') {
    return [
      {
        key: 'all',
        label: '',
        entries: [...entries],
        totalSeconds: sumDurationSeconds(entries),
      },
    ];
  }

  const buckets = new Map<string, TimeEntry[]>();
  for (const e of entries) {
    let bucketKey: string;
    switch (groupKey) {
      case 'designation':
        bucketKey = e.designationId;
        break;
      case 'teammate':
        bucketKey = e.userId;
        break;
      case 'day':
        bucketKey = dayKey(e.startTime.toDate());
        break;
      case 'week':
        bucketKey = weekKey(e.startTime.toDate());
        break;
    }
    const bucket = buckets.get(bucketKey);
    if (bucket) bucket.push(e);
    else buckets.set(bucketKey, [e]);
  }

  const groups: EntryGroup[] = [];
  for (const [key, bucketEntries] of buckets) {
    let label: string;
    switch (groupKey) {
      case 'designation':
        label = lookups.designationLabelsById.get(key) ?? key;
        break;
      case 'teammate':
        label = lookups.userLabelsById.get(key) ?? key;
        break;
      case 'day':
        label = key;
        break;
      case 'week':
        label = key;
        break;
    }
    groups.push({
      key,
      label,
      entries: bucketEntries,
      totalSeconds: sumDurationSeconds(bucketEntries),
    });
  }

  groups.sort((a, b) => {
    if (groupKey === 'day' || groupKey === 'week') {
      // Most recent first (key is YYYY-MM-DD so string compare works).
      return b.key.localeCompare(a.key);
    }
    return a.label.localeCompare(b.label);
  });

  return groups;
}
